/**
 * Interactive "See AgentHQ in Action" product tour configuration.
 *
 * Scenes are driven entirely by this config so the experience can be tuned
 * (copy, callouts, screenshot swaps) without touching the tour component, and
 * so a future recorded demo video can be added via TOUR_VIDEO with no rebuild.
 *
 * Screenshots are STATIC marketing assets captured from the locked Phase 28
 * fictional demo workspace (Jordan Ellis · Ellis & Co. Realty · Houston, TX).
 * They contain only approved fictional data — never real customer/Founder data,
 * admin data, credentials, secrets, or private documents.
 *
 * Callout coordinates are percentages (0–100) of the screenshot box; markers
 * are hidden on very small screens and shown as a chip list instead.
 */

export const TOUR_PERSONA = {
  name: "Jordan Ellis",
  brokerage: "Ellis & Co. Realty",
  location: "Houston, Texas",
  note: "All data shown is a fictional demonstration workspace.",
};

// Calm autoplay pacing (ms per scene) — enough time to read each screen.
export const TOUR_AUTOPLAY_MS = 6500;

// Future recorded-video slot. Leave url null until the Founder adds the
// professionally recorded 1–2 minute AgentHQ demo. When present, the tour
// surfaces a "Watch the demo video" option alongside the interactive tour.
export const TOUR_VIDEO = {
  url: null, // e.g. "/demo-tour/agenthq-demo.mp4" or an embed URL
  poster: null,
  title: "Watch the 1–2 Minute AgentHQ Demo",
  // "primary" => video leads with the interactive tour secondary;
  // "secondary" => interactive tour leads, video available alongside.
  mode: "secondary",
};

export const TOUR_SCENES = [
  {
    id: "dashboard",
    eyebrow: "Scene 1",
    title: "Your Business at a Glance",
    narration: "Start your day knowing exactly what needs your attention.",
    image: "/demo-tour/dashboard.webp",
    alt: "AgentHQ Dashboard showing the Daily Business Brief, Needs Your Attention, Today's Work, and a business snapshot for the demo workspace.",
    callouts: [
      { label: "Daily Business Brief", x: 27, y: 56 },
      { label: "Needs your attention", x: 30, y: 84 },
      { label: "Business snapshot", x: 80, y: 84 },
    ],
  },
  {
    id: "crm",
    eyebrow: "Scene 2",
    title: "Relationships + Leads",
    narration: "Keep your relationships, prospects, and opportunities organized in one place.",
    image: "/demo-tour/crm.webp",
    alt: "AgentHQ CRM showing contacts and leads organized by relationship type for the demo workspace.",
    callouts: [
      { label: "Relationship types", x: 42, y: 36 },
      { label: "Every contact in one place", x: 30, y: 62 },
    ],
  },
  {
    id: "pipeline",
    eyebrow: "Scene 3",
    title: "Pipeline + Smart Follow-Up",
    narration: "Know who needs a follow-up and where every opportunity stands.",
    image: "/demo-tour/pipeline.webp",
    alt: "AgentHQ Pipeline board with stages plus upcoming and overdue follow-ups for the demo workspace.",
    callouts: [
      { label: "Pipeline stages", x: 34, y: 33 },
      { label: "Follow-ups & attention", x: 74, y: 48 },
    ],
  },
  {
    id: "transactions",
    eyebrow: "Scene 4",
    title: "Transactions + Commissions",
    narration: "Move from opportunity to closing while keeping the business side of every deal visible.",
    image: "/demo-tour/transactions.webp",
    alt: "AgentHQ Transactions view showing active and closed deals with closing dates and estimated commissions for the demo workspace.",
    callouts: [
      { label: "Active & closed deals", x: 45, y: 53 },
      { label: "Commission visibility", x: 84, y: 48 },
    ],
  },
  {
    id: "calendar",
    eyebrow: "Scene 5",
    title: "Tasks + Calendar",
    narration: "Turn your business activity into an organized daily workflow.",
    image: "/demo-tour/calendar.webp",
    alt: "AgentHQ Calendar showing closings and follow-ups appearing automatically alongside tasks for the demo workspace.",
    callouts: [
      { label: "Closings appear automatically", x: 60, y: 62 },
      { label: "Follow-ups", x: 76, y: 73 },
    ],
  },
  {
    id: "marketing",
    eyebrow: "Scene 6",
    title: "Marketing",
    narration: "Plan and organize your marketing without disconnecting it from the rest of your business.",
    image: "/demo-tour/marketing.webp",
    alt: "AgentHQ Marketing Center showing brand-grounded content planned and drafted for the demo workspace.",
    callouts: [
      { label: "Grounded in your brand", x: 30, y: 37 },
      { label: "Planned & drafted content", x: 42, y: 60 },
    ],
  },
  {
    id: "compliance",
    eyebrow: "Scene 7",
    title: "Protect",
    narration: "Stay aware of licenses, continuing education, and professional deadlines — organizational tracking, not legal advice.",
    image: "/demo-tour/compliance.webp",
    alt: "AgentHQ License & Compliance showing license, CE, and membership renewal tracking for the demo workspace.",
    callouts: [
      { label: "License & CE status", x: 32, y: 56 },
      { label: "Upcoming renewals", x: 45, y: 72 },
    ],
  },
  {
    id: "analytics",
    eyebrow: "Scene 8",
    title: "Grow",
    narration: "Track where your business is headed with goals and analytics built from your real business data.",
    image: "/demo-tour/analytics.webp",
    alt: "AgentHQ Analytics showing income, closed deals, closed volume, and lead sources for the demo workspace.",
    callouts: [
      { label: "Income & commission", x: 82, y: 40 },
      { label: "Business insights", x: 40, y: 74 },
    ],
  },
  {
    id: "resa",
    eyebrow: "Final scene",
    title: "RESA",
    narration: "RESA brings intelligence into your AgentHQ workspace to help you understand your business and decide what to do next.",
    image: "/demo-tour/resa.webp",
    alt: "RESA, the Real Estate Strategic Assistant, inside the AgentHQ demo workspace preparing next actions for approval.",
    callouts: [
      { label: "RESA, built in", x: 84, y: 30 },
      { label: "Suggests next steps to approve", x: 84, y: 82 },
    ],
  },
];
